import { Modal, Box, Typography, Button, useTheme } from "@mui/material";

interface PackingBoxCountModalProps {
  open: boolean;
  onClose: () => void;
  packingBoxesCount: string | object | null; // This can be either a string or an object
}

const PackingBoxCountModal: React.FC<PackingBoxCountModalProps> = ({
  open,
  onClose,
  packingBoxesCount,
}) => {
  const theme = useTheme();

  // Function to parse the packing box count data if it's a string
  const parseBoxCount = (countData: string | object | null) => {
    if (!countData) return {}; // Return empty object if no data

    if (typeof countData === "string") {
      try {
        // Attempt to parse the string into an object
        const parsed = JSON.parse(countData);
        return parsed;
      } catch (error) {
        console.error("Error parsing box count:", error);
        return {}; // Return empty object if parsing fails
      }
    }

    return countData; // If it's already an object, return it directly
  };

  const boxCounts = parseBoxCount(packingBoxesCount); // Parse the box count data
  const hasBoxCounts = Object.keys(boxCounts).length > 0; // Check if box count data exists

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Box
        sx={{
          width: "80%",
          maxWidth: "600px",
          bgcolor: theme.palette.background.paper,
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
          border: `1px solid ${theme.palette.divider}`,
          overflowY: "auto",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            color: theme.palette.primary.main,
            mb: 3,
            textAlign: "center",
          }}
        >
          Expected Packing Box Count Details
        </Typography>

        <Box sx={{ mb: 3 }}>
          {hasBoxCounts ? (
            // Display each box count type and its value
            Object.entries(boxCounts).map(([boxType, count]) => (
              <Box
                key={boxType}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  padding: "8px",
                  borderRadius: 1,
                  backgroundColor: theme.palette.background.default,
                  boxShadow: 1,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {`${boxType.charAt(0).toUpperCase() + boxType.slice(1)}:`}
                </Typography>
                {/* Ensure the count is a string or number */}
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {count as string | number} {/* Use type assertion here */}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body1" sx={{ mb: 1, textAlign: "center" }}>
              No box count data available.
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: "bold",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PackingBoxCountModal;
